import React from 'react';
import { motion } from "motion/react";
import { CheckIcon } from './Icons';

interface FormInputProps {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  maxLength?: number;
  isConfirmationCode?: boolean;
  isVerified?: boolean;
  helpText?: string;
  actionButton?: React.ReactNode;
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  type,
  value,
  onChange,
  error,
  placeholder,
  maxLength,
  isConfirmationCode = false,
  isVerified = false,
  helpText,
  actionButton
}) => {
  const getInputClasses = () => {
    const baseClasses = "w-full px-4 py-3 bg-gray-900/50 border rounded-2xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 transition-all duration-300";
    
    if (error) {
      return `${baseClasses} border-red-500 focus:ring-red-500/50`;
    }
    
    if (isVerified) {
      return `${baseClasses} border-green-500 focus:ring-green-500/50`;
    }
    
    if (isConfirmationCode && value.length === 6) {
      return `${baseClasses} border-green-500 focus:ring-green-500/50`;
    }
    
    return `${baseClasses} border-gray-700 focus:ring-blue-500/50 focus:border-blue-500`;
  };

  const getInputProps = () => {
    const props: React.InputHTMLAttributes<HTMLInputElement> = {
      type,
      value,
      onChange: (e) => onChange(e.target.value),
      placeholder,
      className: getInputClasses()
    };

    if (maxLength) {
      props.maxLength = maxLength;
    }

    if (isConfirmationCode) {
      props.className += " text-center text-lg tracking-[0.3em] font-mono";
    }

    return props;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      <div className="relative">
        <motion.div whileFocus={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
          <input {...getInputProps()} />
        </motion.div>
        {actionButton && (
          <div className="absolute right-2 top-2">
            {actionButton}
          </div>
        )}
        {isVerified && !actionButton && (
          <motion.div 
            className="absolute right-4 top-3 text-green-500"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <CheckIcon />
          </motion.div>
        )}
        {isConfirmationCode && value.length === 6 && !actionButton && !isVerified && (
          <motion.div 
            className="absolute right-4 top-3 text-green-500"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <CheckIcon />
          </motion.div>
        )}
      </div>
      {helpText && (
        <motion.p 
          className="text-gray-500 text-xs mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {helpText}
        </motion.p>
      )}
      {error && (
        <motion.p 
          className="text-red-400 text-sm mt-1"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};
